// tenant/OrderPages/ViewOrderPage.tsx
import React, { useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import moment from 'moment';
import { useTenantUserInfo } from '../../context/userInfoProvider';

type RouteParams = {
  orderId: string;
};

const ViewOrderPage: React.FC = () => {
  const { orderId } = useParams<RouteParams>();
  const history = useHistory();

  const {
    dataSourceNewOrders = [],
    dataSourcePastOrders = [],
    facilityName,
    facilityAddress,
  } = useTenantUserInfo();

  const order = useMemo(() => {
    const allOrders = [...dataSourceNewOrders, ...dataSourcePastOrders];
    return allOrders.find((o: any) => o.id === orderId);
  }, [orderId, dataSourceNewOrders, dataSourcePastOrders]);

  console.log(orderId)

  if (!order) {
    return (
      <div style={outerStyle}>
        <div style={cardStyle}>
          <h2 style={titleStyle}>Order not found</h2>
          <p style={bodyTextStyle}>
            We couldn’t find an order with that ID. It may have been removed or is still syncing.
          </p>
          <button style={primaryButtonStyle} onClick={() => history.push('/tenant/home')}>
            Back
          </button>
        </div>
      </div>
    );
  }

  const formattedDate = order.date
    ? moment(order.date).format('MMMM DD, YYYY')
    : '—';

  const time = order.time ?? '—';
  const jobType = order.jobType ?? '—';

  const items = Array.isArray(order.orderItems)
    ? order.orderItems.join(', ')
    : (order.orderItems || '—');

  const movingTime =
    typeof order.movingTime === 'number'
      ? `${order.movingTime} min`
      : 'Not yet recorded';

  const movingFee =
    order.movingFee != null
      ? `$${order.movingFee}`
      : 'Not yet calculated';

  const addressFromOrder =
    order.address &&
    typeof order.address === 'object'
      ? [
          order.address.addressLine1,
          order.address.addressLine2,
          `${order.address.city} ${order.address.state} ${order.address.zip}`,
        ]
          .filter(Boolean)
          .join(', ')
      : null;

  const serviceAddress = addressFromOrder || facilityAddress || '—';

  return (
    <div style={outerStyle}>
      <div style={cardStyle}>
        <h1 style={titleStyle}>Order Details</h1>

        {/* Summary row */}
        <div style={summaryRowStyle}>
          <div>
            <div style={summaryLabelStyle}>Date</div>
            <div style={summaryValueStyle}>{formattedDate}</div>
          </div>
          <div>
            <div style={summaryLabelStyle}>Time Window</div>
            <div style={summaryValueStyle}>{time}</div>
          </div>
          <div>
            <div style={summaryLabelStyle}>Type</div>
            <div style={summaryValueStyle}>{jobType}</div>
          </div>
        </div>

        {/* Sections */}
        <Section label="Service address" value={serviceAddress} />
        <Section label="Storage facility" value={facilityName || 'CSI Mini Storage'} />
        <Section label="Items" value={items} />
        <Section label="Moving time" value={movingTime} />
        <Section label="Total moving fees" value={movingFee} />

        <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
          <button
            type="button"
            style={secondaryButtonStyle}
            onClick={() => history.goBack()}
          >
            Back
          </button>
          <button
            type="button"
            style={primaryButtonStyle}
            onClick={() => history.push('/tenant/orders')}
          >
            View all orders
          </button>
        </div>
      </div>
    </div>
  );
};

type SectionProps = {
  label: string;
  value: React.ReactNode;
};

const Section: React.FC<SectionProps> = ({ label, value }) => (
  <div style={sectionStyle}>
    <div style={sectionLabelStyle}>{label}</div>
    <div style={sectionValueStyle}>{value}</div>
  </div>
);

// ---- styles (aligned with signup / tenant home look) ----
const outerStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: 'radial-gradient(circle at top, #3b1c58, #261136)',
  padding: '32px 16px',
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
};

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 720,
  backgroundColor: '#1b1027',
  borderRadius: 16,
  padding: 24,
  boxShadow: '0 12px 30px rgba(0,0,0,0.35)',
  color: '#f5f5ff',
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  marginBottom: 16,
  fontSize: 24,
  fontWeight: 600,
};

const bodyTextStyle: React.CSSProperties = {
  fontSize: 14,
  lineHeight: 1.5,
  marginBottom: 16,
};

const summaryRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap: 12,
  padding: 12,
  borderRadius: 12,
  backgroundColor: '#2b1840',
  marginBottom: 20,
};

const summaryLabelStyle: React.CSSProperties = {
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  opacity: 0.7,
};

const summaryValueStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 500,
};

const sectionStyle: React.CSSProperties = {
  padding: '10px 0',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
};

const sectionLabelStyle: React.CSSProperties = {
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  opacity: 0.7,
  marginBottom: 4,
};

const sectionValueStyle: React.CSSProperties = {
  fontSize: 14,
  lineHeight: 1.5,
};

const primaryButtonStyle: React.CSSProperties = {
  padding: '10px 18px',
  borderRadius: 999,
  border: 'none',
  background:
    'linear-gradient(135deg, #7B1FA2, #b36bff)',
  color: 'white',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: '10px 18px',
  borderRadius: 999,
  border: '1px solid rgba(255,255,255,0.3)',
  background: 'transparent',
  color: 'white',
  fontSize: 14,
  fontWeight: 400,
  cursor: 'pointer',
};

export default ViewOrderPage;
